import { Box } from '@mui/material';
import { useState, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SerializedError } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { analytics, events } from 'services/analytics';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import useReleaseNavigation from 'services/hooks/distribution/useReleaseNavigation';
import {
  useAuthStore,
  useMessageSnackbar,
  useSubscription,
} from 'services/hooks';
import { ReleaseLimitDialog } from 'components/Modals/implementations/ReleaseLimitDialog';
import { useCreateRelease } from 'services/hooks/distribution';
import { locations } from 'services/routes/locations';
import { selectReleaseRequest } from 'services/store/distribution/selectors';
import {
  isReleaseLimitExceededError,
  isCopyrightViolationError,
  isReleaseError,
} from 'services/helpers/error';
import UpsellModal from 'components/Modals/UpsellModal';
import UpgradeDistributionModal from 'components/Modals/UpgradeModal';
import {
  CopyrightViolationType,
  CopyrightViolationModal,
} from 'components/Modals/CopyrightViolationModal';
import DistributionWarningModal from 'components/Modals/DistributionWarningModal';
import { selectIsSubmitReleaseLimitReached } from 'services/store/user/selectors';
import useAnalytics from 'services/hooks/analytics/';

interface UseReleaseProps {
  children: ReactNode;
  songId: string;
  location: AnalyticsEventLocation;
}

const ReleaseButtonContainer = ({
  children,
  songId,
  location,
}: UseReleaseProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateToReleaseFlow = useReleaseNavigation();
  const { displayErrorSnackbar } = useMessageSnackbar();
  const { track } = useAnalytics();

  const [releaseError, setReleaseError] = useState<SerializedError | null>(
    null
  );

  const [isCopyrightViolationModalOpen, setIsCopyrightViolationModalOpen] =
    useState(false);

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isReleaseLimitDialogOpen, setIsReleaseLimitDialogOpen] =
    useState(false);
  const isReleaseLimitReached = useSelector(selectIsSubmitReleaseLimitReached);

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;

  const { isFreeAccount } = useSubscription();

  const { createNewRelease, data, error } = useCreateRelease({
    hasUserAudio: false,
  });

  const releaseRequest = useSelector(selectReleaseRequest(songId));

  useEffect(() => {
    if (!data?.id) {
      return;
    }
    navigateToReleaseFlow(data.id);
  }, [data, navigateToReleaseFlow]);

  const handleClick = useCallback(() => {
    track(events.releaseButtonClicked(location));

    if (!isLoggedIn) {
      navigate(locations.general.register.pathname(''));
      return;
    }

    if (isFreeAccount) {
      setIsUpgradeModalOpen(true);
      return;
    }

    if (isReleaseLimitReached) {
      setIsReleaseLimitDialogOpen(true);
      return;
    }

    if (releaseRequest?.data && data) {
      navigateToReleaseFlow(releaseRequest.data);
      return;
    }

    createNewRelease({ songId });
  }, [
    track,
    location,
    isLoggedIn,
    navigate,
    isFreeAccount,
    setIsUpgradeModalOpen,
    isReleaseLimitReached,
    setIsReleaseLimitDialogOpen,
    releaseRequest,
    data,
    createNewRelease,
    songId,
    navigateToReleaseFlow,
  ]);

  useEffect(() => {
    if (!error) {
      return;
    }
    analytics.track(events.createReleaseError());
    if (isReleaseError(error)) {
      setReleaseError(error);
      return;
    }
    if (isReleaseLimitExceededError(error)) {
      setIsUpsellModalOpen(true);
      return;
    }
    if (isCopyrightViolationError(error)) {
      setIsCopyrightViolationModalOpen(true);
      return;
    }
    displayErrorSnackbar(t('messages:error:unknown'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, error]);

  const closeWarningModal = useCallback(() => {
    setReleaseError(null);
  }, []);

  const closeUpsellModal = useCallback(() => {
    setIsUpsellModalOpen(false);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false);
  }, []);

  const closeCopyrightViolationModal = useCallback(() => {
    setIsCopyrightViolationModalOpen(false);
  }, []);

  const closeReleaseLimitDialog = () => {
    setIsReleaseLimitDialogOpen(false);
  };

  return (
    <>
      <Box onClick={handleClick}>{children}</Box>

      <ReleaseLimitDialog
        isOpen={isReleaseLimitDialogOpen}
        onClose={closeReleaseLimitDialog}
      />
      <DistributionWarningModal
        error={releaseError}
        onClose={closeWarningModal}
      />
      <CopyrightViolationModal
        variant={CopyrightViolationType.RELEASE}
        isOpen={isCopyrightViolationModalOpen}
        onClose={closeCopyrightViolationModal}
      />
      <UpsellModal
        isOpen={isUpsellModalOpen}
        onClose={closeUpsellModal}
        location={location}
        songId={songId}
      />
      <UpgradeDistributionModal
        isOpen={isUpgradeModalOpen}
        onClose={closeUpgradeModal}
        location={location}
      />
    </>
  );
};

export default ReleaseButtonContainer;
